import React, { useCallback } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useObservable } from "micro-observables";
import { AnimatePresence } from "framer-motion";
import { useUser } from "@domain/user/hooks/use-user";
import { e2eTestId } from "@e2e/test-ids";
import { isNotNil } from "@utils/assert";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { patientProgramsManager } from "@core/service-container";
import { PatientProgramCard } from "@ui/components/program/program-card";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { EmptyPrograms } from "@ui/pages/program/components/empty-programs";
import { EmptyProgramsSearch } from "@ui/pages/program/components/empty-programs-search";
import { useProgramFilters } from "@ui/pages/program/hooks/use-program-filters";
import { ProgramSortKey, useProgramSort } from "@ui/pages/program/hooks/use-program-sort";
import { SearchFilter } from "@ui/pages/program/components/search-filter";
import { SortDropdown } from "@ui/pages/program/components/sort-dropdown";
import { useProgramCard } from "@ui/components/program/hooks/use-program-card";
import { EmptyContainer, Layout, TabContainer, TabHeader, CardWrapper } from "@ui/pages/program/components/tabs.styles";
import { ProgramListTabNames } from "@ui/pages/program/components/tabs";

export const ProgramsTab: React.FC<{
	selectTab: (tabName: ProgramListTabNames) => void;
}> = ({ selectTab }) => {
	const { format } = useI18n();
	const user = useUser();
	const { isDesktop } = useDimensions();
	const programs = useObservable(patientProgramsManager.allPatientsPrograms);
	const { lastProgramAdded, navigateToProgram, getProgramCardActions } = useProgramCard();
	const { allFilters, setSearchFilter, filteredPrograms, programTotalCount } = useProgramFilters(programs);
	const { allSortOptions, setSortKey, sortedPrograms: sortedFilteredPrograms } = useProgramSort(filteredPrograms);

	const filterPrograms = useCallback(
		(search: string, activeFilters: ProgramStatus[]) => {
			setSearchFilter({ search, filters: activeFilters });
		},
		[setSearchFilter],
	);

	const sortPrograms = useCallback((key: ProgramSortKey) => setSortKey(key), [setSortKey]);

	return programTotalCount === 0 ? (
		<EmptyContainer>
			<EmptyPrograms onSecondaryClick={() => selectTab(ProgramListTabNames.Templates)} />
		</EmptyContainer>
	) : (
		<TabContainer>
			<TabHeader>
				<SearchFilter
					placeholder={format("templateListing.programs.searchInputPlaceholder")}
					filters={allFilters}
					onChange={filterPrograms}
					defaultActiveFilters={[ProgramStatus.Ongoing]}
				/>
				{isDesktop && <SortDropdown sortOptions={allSortOptions} onChange={sortPrograms} />}
			</TabHeader>
			{sortedFilteredPrograms.length === 0 ? (
				<EmptyContainer>
					<EmptyProgramsSearch />
				</EmptyContainer>
			) : (
				<Layout data-test={e2eTestId.createTemplatePage.programGrid}>
					<AnimatePresence presenceAffectsLayout>
						{isNotNil(user) &&
							sortedFilteredPrograms.map(program => (
								<CardWrapper key={program.id} layoutId={program.id}>
									<PatientProgramCard
										patient={program.patient}
										program={program}
										cardActions={getProgramCardActions(program.patient, program, () =>
											selectTab(ProgramListTabNames.Templates),
										)}
										onClick={() => navigateToProgram(program.patient, program)}
										isNew={program.id === lastProgramAdded}
									/>
								</CardWrapper>
							))}
					</AnimatePresence>
				</Layout>
			)}
		</TabContainer>
	);
};
